import { useCallback, useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { callSyncStatus } from "../lib/api";
import { isFull } from "../lib/auth";
import { useToast } from "../components/Toast";
import { derivarStageExibicao, diasParado } from "../lib/stages";
import type { BoardRow } from "../lib/types";
import { BoardFilters, type Filtros } from "../components/BoardFilters";
import { BoardKanban } from "../components/BoardKanban";
import { BoardTable } from "../components/BoardTable";
import styles from "./Board.module.css";

type Vista = "kanban" | "tabela";

const VISTA_KEY = "ru-board-vista";

export function Board({ session }: { session: Session }) {
  const { show } = useToast();
  const full = isFull(session);
  const [rows, setRows] = useState<BoardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [vista, setVista] = useState<Vista>(
    () => (localStorage.getItem(VISTA_KEY) as Vista) || "kanban"
  );
  const [filtros, setFiltros] = useState<Filtros>({ busca: "", etapa: null, paradoDias: null });

  const carregar = useCallback(async () => {
    const { data, error } = await supabase
      .from("v_onboarding_board")
      .select("*")
      .order("atualizado_em", { ascending: false });
    if (error) {
      show("error", `Falha ao carregar o board: ${error.message}`);
    } else {
      setRows((data ?? []) as BoardRow[]);
    }
    setLoading(false);
  }, [show]);

  useEffect(() => {
    carregar();
    // Realtime: o board atualiza ao vivo quando o pipeline muda.
    const canal = supabase
      .channel("board-pipeline")
      .on("postgres_changes", { event: "*", schema: "public", table: "onboarding_pipeline" }, () => {
        carregar();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(canal);
    };
  }, [carregar]);

  function trocarVista(v: Vista) {
    setVista(v);
    localStorage.setItem(VISTA_KEY, v);
  }

  async function sincronizar() {
    setSincronizando(true);
    const r = await callSyncStatus();
    if (r.ok) {
      show("success", "Sincronização disparada. O board atualiza ao concluir.");
      // Relê após alguns segundos para refletir mudanças não cobertas pelo realtime.
      setTimeout(carregar, 4000);
    } else {
      show("error", `Falha ao sincronizar: ${r.message ?? "erro desconhecido"}`);
    }
    setSincronizando(false);
  }

  const filtradas = useMemo(() => {
    const q = filtros.busca.trim().toLowerCase();
    return rows.filter((r) => {
      // Busca casa tanto o nome do projeto quanto a razão social.
      if (q && !`${r.nome_projeto ?? ""} ${r.cliente_nome ?? ""}`.toLowerCase().includes(q)) return false;
      if (filtros.etapa != null && derivarStageExibicao(r) !== filtros.etapa) return false;
      if (filtros.paradoDias != null) {
        const d = diasParado(r.atualizado_em);
        if (d == null || d < filtros.paradoDias) return false;
      }
      return true;
    });
  }, [rows, filtros]);

  return (
    <div>
      <div className={styles.head}>
        <div>
          <h1>Clientes</h1>
          <p className="muted">
            {loading ? "Carregando…" : `${filtradas.length} de ${rows.length} clientes`}
          </p>
        </div>
        <div className={styles.actions}>
          <div className={styles.toggle} role="tablist" aria-label="Modo de visualização">
            <button
              className={`${styles.toggleBtn} ${vista === "kanban" ? styles.toggleOn : ""}`}
              onClick={() => trocarVista("kanban")}
            >
              Kanban
            </button>
            <button
              className={`${styles.toggleBtn} ${vista === "tabela" ? styles.toggleOn : ""}`}
              onClick={() => trocarVista("tabela")}
            >
              Tabela
            </button>
          </div>
          <button className="btn btn-ghost" onClick={sincronizar} disabled={sincronizando}>
            {sincronizando ? "Sincronizando…" : "Sincronizar status"}
          </button>
        </div>
      </div>

      <BoardFilters filtros={filtros} onChange={setFiltros} />

      {loading ? (
        <div className="splash">Carregando board…</div>
      ) : vista === "kanban" ? (
        <BoardKanban rows={filtradas} mostrarValor={full} />
      ) : (
        <BoardTable rows={filtradas} mostrarValor={full} />
      )}
    </div>
  );
}
